import {BookOpen, CheckCircle, DollarSign} from 'lucide-react'

const GUIDE_STEPS = [
  {
    title: "Calculate Total Revenue",
    content:
      "Start with your calf sales: multiply the number of calves weaned by their average sale weight and current market price per pound. Add cull cow revenue — typically 10-15% of your herd is culled annually, selling at $0.75-$0.95/lb at approximately 1,100-1,200 lbs.",
  },
  {
    title: "Tally All Expense Categories",
    content:
      "Feed and hay typically represent 50-65% of total costs. Include pasture/land costs, veterinary and health expenses, breeding/AI fees, labor (even if unpaid family labor — assign a fair market value), equipment depreciation, mineral supplements, insurance, and marketing/hauling fees.",
  },
  {
    title: "Compute Per-Head Profitability",
    content:
      "Subtract total expenses from total revenue to get net profit. Divide by herd size for profit per cow. A healthy cow-calf operation targets $100-$300+ per cow in net profit. Anything below break-even signals a need for cost restructuring or revenue improvement.",
  },
  {
    title: "Determine Break-Even Price",
    content:
      "Your break-even price is the minimum market price per pound of calf needed to cover all costs. Calculate it as: (Total Expenses - Cull Revenue) / (Calves Weaned x Avg Sale Weight). If the current market price exceeds your break-even, you are profitable.",
  },
  {
    title: "Optimize the Key Levers",
    content:
      "The three most impactful levers for cow-calf profitability are: (1) calving rate — every 1% increase adds significant revenue, (2) feed cost management — reducing waste and optimizing grazing, and (3) market timing — selling at seasonal price peaks rather than troughs.",
  },
]

const KEY_FACTS = [
  "Feed costs represent 50-65% of total cow-calf expenses",
  "A 90%+ calving rate is considered excellent for commercial herds",
  "Cull cow revenue typically covers 15-20% of annual cow costs",
  "Break-even calf prices for most operations range from $1.80-$2.50/lb",
  "Every 1% increase in calving rate adds ~$15-25/cow in revenue",
  "Rotational grazing can reduce feed costs by 15-25%",
  "Top-third producers earn $150-300+ more per cow than bottom-third",
]

const BENCHMARKS = [
  { metric: "Calving Rate", top: "93-96%", avg: "85-90%", bottom: "<82%" },
  { metric: "Feed Cost/Cow", top: "<$500", avg: "$550-700", bottom: ">$750" },
  { metric: "Total Cost/Cow", top: "<$850", avg: "$950-1,150", bottom: ">$1,250" },
  { metric: "Profit/Cow", top: ">$200", avg: "$50-150", bottom: "<$0" },
  { metric: "Break-Even ($/lb)", top: "<$1.80", avg: "$2.00-2.40", bottom: ">$2.60" },
]

export default function CowCalfProfitGuide() {
  return (
    <section id="profit-guide" className="py-12 sm:py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-14">
          <span className="inline-block text-[#40916C] text-xs sm:text-sm font-semibold tracking-widest uppercase mb-3">
            Expert Resource
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#283618]">
            How to Calculate Cow Calf Profitability
          </h2>
          <p className="mt-3 text-[#606C38] text-base sm:text-lg max-w-2xl mx-auto">
            A comprehensive guide to analyzing your cow-calf operation's revenue, expenses, and profit margins. Understand the key financial metrics that separate profitable ranches from struggling operations.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Steps */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-2 mb-6">
              <BookOpen className="w-5 h-5 text-[#2D6A4F]" />
              <h3 className="font-bold text-lg text-[#283618]" style={{ fontFamily: "'Merriweather', serif" }}>
                Step-by-Step Profit Analysis
              </h3>
            </div>

            <div className="space-y-0">
              {GUIDE_STEPS.map((step, index) => (
                <div key={step.title} className="relative flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-9 h-9 rounded-full bg-[#D4A373] text-[#283618] flex items-center justify-center font-bold text-sm shrink-0 z-10">
                      {index + 1}
                    </div>
                    {index < GUIDE_STEPS.length - 1 && (
                      <div className="w-0.5 flex-1 bg-[#D4A373]/30 my-1" />
                    )}
                  </div>
                  <div className="pb-8">
                    <h4 className="font-bold text-[#283618] text-base mb-1.5">{step.title}</h4>
                    <p className="text-sm sm:text-base text-[#606C38] leading-relaxed">{step.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-[#FEFAE0] rounded-2xl border border-[#B7E4C7]/40 p-6 shadow-sm sticky top-24">
              <h3 className="font-bold text-lg text-[#283618] mb-4" style={{ fontFamily: "'Merriweather', serif" }}>
                Key Profitability Facts
              </h3>
              <ul className="space-y-3">
                {KEY_FACTS.map((fact) => (
                  <li key={fact} className="flex items-start gap-2.5">
                    <CheckCircle className="w-4 h-4 text-[#40916C] shrink-0 mt-0.5" />
                    <span className="text-sm text-[#606C38] leading-relaxed">{fact}</span>
                  </li>
                ))}
              </ul>

              {/* Benchmark Table */}
              <div className="mt-6 pt-5 border-t border-[#B7E4C7]/30">
                <h4 className="font-semibold text-sm text-[#283618] mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-[#D4A373]" />
                  Industry Benchmarks
                </h4>
                <div className="space-y-0">
                  {/* Header */}
                  <div className="flex items-center text-[10px] font-bold uppercase tracking-wider text-[#606C38] mb-2">
                    <span className="flex-1">Metric</span>
                    <span className="w-16 text-center text-[#2D6A4F]">Top 1/3</span>
                    <span className="w-16 text-center text-[#D4A373]">Average</span>
                    <span className="w-16 text-center text-[#E76F51]">Bottom</span>
                  </div>
                  {BENCHMARKS.map((b) => (
                    <div key={b.metric} className="flex items-center text-xs py-1.5 border-t border-[#B7E4C7]/20">
                      <span className="flex-1 font-medium text-[#283618]">{b.metric}</span>
                      <span className="w-16 text-center text-[#2D6A4F] font-semibold">{b.top}</span>
                      <span className="w-16 text-center text-[#D4A373]">{b.avg}</span>
                      <span className="w-16 text-center text-[#E76F51]">{b.bottom}</span>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[10px] text-[#606C38]/60 leading-relaxed">
                  Benchmarks based on USDA ERS Cow-Calf data and university extension budgets. Actual performance varies by region and management.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
